const REDIRECT_URL_CONTACT_FIELDS = [
  { attribute: "firstname", personKey: "name", label: "First name" },
  { attribute: "lastname", personKey: "last_name", label: "Last name" },
  { attribute: "email", personKey: "email", label: "Email" },
  { attribute: "phone", personKey: "phone", label: "Phone" },
  { attribute: "website", personKey: "url", label: "Website" },
  {
    attribute: "company_name",
    personKey: "company_name",
    label: "Company name",
  },
  {
    attribute: "company_title",
    personKey: "company_title",
    label: "Company title",
  },
  { attribute: "city", personKey: "city", label: "City" },
  { attribute: "country", personKey: "country", label: "Country" },
  { attribute: "address", personKey: "address", label: "Address" },
  { attribute: "state", personKey: "state", label: "State" },
  { attribute: "zipcode", personKey: "zip_code", label: "Zip code" },
];

class RedirectUrlFieldsService {
  static contactFields() {
    return REDIRECT_URL_CONTACT_FIELDS;
  }

  static contactField(attribute) {
    return REDIRECT_URL_CONTACT_FIELDS.find(
      (contact) => contact.attribute === attribute
    );
  }

  static contactLabel(attribute) {
    const contact = RedirectUrlFieldsService.contactField(attribute);
    return contact ? contact.label : attribute;
  }

  static parseToken(decoded) {
    const match = decoded && decoded.match(/^\(\{(.+)\}\)$/);
    if (!match) return null;

    const inner = match[1];
    if (inner === "CURRENT_SCORE") return { type: "score" };

    const accountMatch = inner.match(/^person\.fields\.(.+)$/);
    if (accountMatch) return { type: "account_field", slug: accountMatch[1] };

    const contactMatch = inner.match(/^person\.([a-z_]+)$/);
    if (
      contactMatch &&
      RedirectUrlFieldsService.contactField(contactMatch[1])
    ) {
      return { type: "contact_field", attribute: contactMatch[1] };
    }

    return { type: "unknown" };
  }

  static buildToken(badge) {
    if (badge.type === "account_field")
      return "({person.fields." + badge.slug + "})";
    if (badge.type === "contact_field")
      return "({person." + badge.attribute + "})";
    if (badge.type === "score") return "({CURRENT_SCORE})";
    return null;
  }

  static parse(value) {
    if (!value) return { text: "", badges: [] };

    const url = value.replace(/&amp;/g, "&");
    const { beforeFragment, fragment } =
      RedirectUrlFieldsService.splitFragment(url);

    const queryIndex = beforeFragment.indexOf("?");
    if (queryIndex === -1) return { text: url, badges: [] };

    const badges = [];
    const staticSegments = [];

    beforeFragment
      .slice(queryIndex + 1)
      .split("&")
      .forEach((segment) => {
        if (segment === "") return;

        const badge = RedirectUrlFieldsService.segmentBadge(segment);
        if (badge) {
          badges.push(badge);
        } else {
          staticSegments.push(segment);
        }
      });

    const base = beforeFragment.slice(0, queryIndex);
    const query = staticSegments.length ? "?" + staticSegments.join("&") : "";

    return { text: base + query + fragment, badges: badges };
  }

  static serialize(text, badges) {
    const cleanText = (text || "").replace(/[?&]+$/, "");
    if (!badges || !badges.length) return cleanText;

    const segments = badges.map(RedirectUrlFieldsService.badgeSegment);
    return RedirectUrlFieldsService.appendQuery(cleanText, segments.join("&"));
  }

  static parseRaw(value) {
    const trailingToken = /\(\{[^{}]+\}\)\s*$/;
    const badges = [];
    let text = value || "";

    let match = text.match(trailingToken);
    while (match) {
      const token = RedirectUrlFieldsService.parseToken(match[0].trim());
      if (!token) break;

      badges.unshift(
        RedirectUrlFieldsService.badgeFromToken(null, match[0].trim(), token)
      );
      text = text.slice(0, text.length - match[0].length);
      match = text.match(trailingToken);
    }

    return { text: text, badges: badges };
  }

  static serializeRaw(text, badges) {
    const tokens = (badges || []).map((badge) =>
      badge.type === "unknown"
        ? badge.raw
        : RedirectUrlFieldsService.buildToken(badge)
    );
    return (text || "") + tokens.join("");
  }

  static resolve(url, context) {
    const parsed = RedirectUrlFieldsService.parse(url);
    if (!parsed.badges.length) return parsed.text;

    const existingKeys = RedirectUrlFieldsService.queryKeys(parsed.text);
    const segments = [];

    parsed.badges.forEach((badge) => {
      const key = badge.key || RedirectUrlFieldsService.autoKey(badge);
      if (!key || existingKeys[key]) return;

      RedirectUrlFieldsService.badgeValues(badge, context).forEach((value) => {
        segments.push(RedirectUrlFieldsService.querySegment(key, value));
      });
    });

    if (!segments.length) return parsed.text;
    return RedirectUrlFieldsService.appendQuery(
      parsed.text,
      segments.join("&")
    );
  }

  static appendAnswers(url, answers, fields, coveredSlugs) {
    const existingKeys = RedirectUrlFieldsService.queryKeys(url);
    const paramNames = RedirectUrlFieldsService.fieldParamNames(fields);
    const segments = [];

    Object.keys(answers || {}).forEach((slugOrId) => {
      const paramKey = paramNames[slugOrId] || slugOrId;
      if (existingKeys[paramKey]) return;
      if (coveredSlugs && coveredSlugs[slugOrId]) return;

      RedirectUrlFieldsService.presentValues(answers[slugOrId]).forEach(
        (value) => {
          segments.push(RedirectUrlFieldsService.querySegment(paramKey, value));
        }
      );
    });

    if (!segments.length) return url;
    return RedirectUrlFieldsService.appendQuery(url, segments.join("&"));
  }

  static autoKey(badge) {
    if (badge.type === "score") return "score";
    if (badge.type === "account_field") return badge.slug;
    if (badge.type === "contact_field") return badge.attribute;
    return null;
  }

  static slugOrId(field) {
    return field.slug && field.slug !== "New Field"
      ? field.slug
      : "cf_question_id_" + field.id;
  }

  static queryKeys(url) {
    const keys = {};
    if (!url) return keys;

    const { beforeFragment } = RedirectUrlFieldsService.splitFragment(url);
    const queryIndex = beforeFragment.indexOf("?");
    if (queryIndex === -1) return keys;

    beforeFragment
      .slice(queryIndex + 1)
      .split("&")
      .forEach((segment) => {
        if (segment === "") return;

        const eq = segment.indexOf("=");
        const key = RedirectUrlFieldsService.safeDecode(
          eq === -1 ? segment : segment.slice(0, eq)
        );
        if (key) keys[key] = true;
      });

    return keys;
  }

  static appendQuery(url, query) {
    const { beforeFragment, fragment } =
      RedirectUrlFieldsService.splitFragment(url);
    const joiner = beforeFragment.indexOf("?") === -1 ? "?" : "&";
    return beforeFragment + joiner + query + fragment;
  }

  static segmentBadge(segment) {
    const wholeToken = RedirectUrlFieldsService.parseToken(
      RedirectUrlFieldsService.safeDecode(segment)
    );
    if (wholeToken) {
      return RedirectUrlFieldsService.badgeFromToken(null, segment, wholeToken);
    }

    const eq = segment.indexOf("=");
    if (eq === -1) return null;

    const valueToken = RedirectUrlFieldsService.parseToken(
      RedirectUrlFieldsService.safeDecode(segment.slice(eq + 1))
    );
    if (!valueToken) return null;

    return RedirectUrlFieldsService.badgeFromToken(
      RedirectUrlFieldsService.safeDecode(segment.slice(0, eq)),
      segment,
      valueToken
    );
  }

  static badgeFromToken(key, raw, token) {
    const badge = { key: key, raw: raw, type: token.type };
    if (token.slug !== undefined) badge.slug = token.slug;
    if (token.attribute !== undefined) badge.attribute = token.attribute;
    return badge;
  }

  static badgeSegment(badge) {
    if (badge.type === "unknown") return badge.raw;

    const token = encodeURIComponent(
      RedirectUrlFieldsService.buildToken(badge)
    );
    return badge.key ? encodeURIComponent(badge.key) + "=" + token : token;
  }

  static badgeValues(badge, context) {
    if (badge.type === "score") {
      return RedirectUrlFieldsService.presentValues(context.score);
    }
    if (badge.type === "account_field") {
      return RedirectUrlFieldsService.accountFieldValues(badge.slug, context);
    }
    if (badge.type === "contact_field") {
      return RedirectUrlFieldsService.contactFieldValues(
        badge.attribute,
        context
      );
    }
    return [];
  }

  static accountFieldValues(slug, context) {
    const answers = RedirectUrlFieldsService.presentValues(
      context.answers && context.answers[slug]
    );
    if (answers.length) return answers;

    const urlValue = context.getParam ? context.getParam(slug) : "";
    if (urlValue !== "") return [urlValue];

    return RedirectUrlFieldsService.presentValues(
      context.personExtra && context.personExtra[slug]
    );
  }

  static contactFieldValues(attribute, context) {
    const person = context.person || {};
    const contact = RedirectUrlFieldsService.contactField(attribute);

    const stored = RedirectUrlFieldsService.presentValues(
      contact && person[contact.personKey]
    );
    if (stored.length) return stored;

    const fallback = RedirectUrlFieldsService.presentValues(person[attribute]);
    if (fallback.length) return fallback;

    const urlValue = context.getParam ? context.getParam(attribute) : "";
    return urlValue === "" ? [] : [urlValue];
  }

  static fieldParamNames(fields) {
    const names = {};

    Object.values(fields || {}).forEach((field) => {
      const slugOrId = RedirectUrlFieldsService.slugOrId(field);
      names[slugOrId] = field.parameter ? field.parameter : slugOrId;
    });

    return names;
  }

  static presentValues(stored) {
    const values = Array.isArray(stored) ? stored : [stored];
    return values.filter(
      (value) => value !== undefined && value !== null && value !== ""
    );
  }

  static querySegment(key, value) {
    return encodeURIComponent(key) + "=" + encodeURIComponent(value);
  }

  static splitFragment(url) {
    const hashIndex = url.indexOf("#");
    if (hashIndex === -1) return { beforeFragment: url, fragment: "" };

    return {
      beforeFragment: url.slice(0, hashIndex),
      fragment: url.slice(hashIndex),
    };
  }

  static safeDecode(value) {
    try {
      return decodeURIComponent(value);
    } catch (error) {
      return null;
    }
  }
}

if (typeof window !== "undefined") {
  window.RedirectUrlFieldsService = RedirectUrlFieldsService;
}

export default RedirectUrlFieldsService;
